/**
 * Asset Detail Page - Master Record View
 * Tabs: Overview, PPM Schedule, Jobs, Documents, Activity
 * Shows compliance summary and all linked data
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, Building2, MapPin, Calendar, FileText, Briefcase, 
  Clock, AlertTriangle, CheckCircle, Upload, Eye, Download,
  Activity, Flame, Zap, Droplet, Shield, Home, Users, Plus,
  AlertOctagon, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Document types
const DOCUMENT_TYPES = [
  'EICR',
  'Gas Safety Certificate',
  'Boiler Service Certificate',
  'Legionella Report',
  'Fire / Alarm Certificate',
  'Rental Agreement',
  'Photos',
  'General Attachment'
];

const DOC_TYPE_ICONS = {
  'EICR': Zap,
  'Gas Safety Certificate': Flame,
  'Boiler Service Certificate': Flame,
  'Legionella Report': Droplet,
  'Fire / Alarm Certificate': Shield,
  'Rental Agreement': FileText,
  'Photos': Eye,
  'General Attachment': FileText
};

// Job status colors
const STATUS_COLORS = {
  'Job Created': 'bg-blue-100 text-blue-700',
  'Awaiting Quote': 'bg-purple-100 text-purple-700',
  'Quote Awaiting Approval': 'bg-amber-100 text-amber-700',
  'Approved': 'bg-green-100 text-green-700',
  'Assigned': 'bg-cyan-100 text-cyan-700',
  'Scheduled': 'bg-teal-100 text-teal-700',
  'Completed': 'bg-emerald-100 text-emerald-700',
  'Closed': 'bg-slate-100 text-slate-600',
};

export default function AssetDetailPage() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Related data
  const [jobs, setJobs] = useState([]);
  const [ppmItems, setPpmItems] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activity, setActivity] = useState([]);
  
  // Dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    document_type: '',
    expiry_date: '',
    file: null
  });
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  const loadAsset = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/ppm-core/assets/${assetId}`, { headers });
      setAsset(res.data.asset || res.data);
      setError(null);
    } catch (err) {
      console.error('Load asset error:', err);
      setError('Failed to load asset details');
      toast.error('Failed to load asset');
    } finally {
      setLoading(false);
    }
  }, [assetId]);
  
  const loadJobs = async () => {
    try {
      const res = await axios.get(`${API}/api/fm/jobs?asset_id=${assetId}&limit=100`, { headers });
      setJobs(res.data.jobs || []);
    } catch (err) {
      console.error('Load jobs error:', err);
    }
  };
  
  const loadPPMItems = async () => {
    try {
      const res = await axios.get(`${API}/api/ppm-core/assigned?asset_id=${assetId}`, { headers });
      setPpmItems(res.data.assigned || []);
    } catch (err) {
      console.error('Load PPM error:', err);
    }
  };
  
  const loadDocuments = async () => {
    try {
      const res = await axios.get(`${API}/api/ppm-core/assets/${assetId}/documents`, { headers });
      setDocuments(res.data.documents || []);
    } catch (err) {
      console.error('Load documents error:', err);
      // Set empty array on error
      setDocuments([]);
    }
  };
  
  const loadActivity = async () => {
    try {
      const res = await axios.get(`${API}/api/ppm-core/assets/${assetId}/activity`, { headers });
      setActivity(res.data.activity || []);
    } catch (err) {
      console.error('Load activity error:', err);
      setActivity([]);
    }
  };
  
  useEffect(() => {
    loadAsset();
    loadJobs();
    loadPPMItems();
    loadDocuments();
    loadActivity();
  }, [loadAsset]);
  
  // ==========================================================================
  // COMPLIANCE CALCULATIONS
  // ==========================================================================
  
  const getNextDueDate = (docType) => {
    const doc = documents.find(d => d.document_type === docType && d.expiry_date);
    if (doc?.expiry_date) {
      const expiry = parseISO(doc.expiry_date);
      const daysUntil = differenceInDays(expiry, new Date());
      return { date: doc.expiry_date, daysUntil, isOverdue: daysUntil < 0 };
    }
    return { date: null, daysUntil: null, isOverdue: false };
  };
  
  const eicrDue = getNextDueDate('EICR');
  const gasDue = getNextDueDate('Gas Safety Certificate');
  const boilerDue = getNextDueDate('Boiler Service Certificate');
  
  const openJobsCount = jobs.filter(j => !['Completed', 'Closed'].includes(j.status)).length;
  const overduePPMCount = ppmItems.filter(p => {
    if (!p.next_due_date) return false;
    return differenceInDays(new Date(), parseISO(p.next_due_date)) > 0;
  }).length;
  
  // ==========================================================================
  // DOCUMENT UPLOAD
  // ==========================================================================
  
  const handleUploadDocument = async () => {
    if (!uploadData.document_type) {
      toast.error('Please select document type');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('document_type', uploadData.document_type);
      if (uploadData.expiry_date) {
        formData.append('expiry_date', uploadData.expiry_date);
      }
      if (uploadData.file) {
        formData.append('file', uploadData.file);
      }
      
      await axios.post(`${API}/api/ppm-core/assets/${assetId}/documents`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploaded');
      setShowUploadDialog(false);
      setUploadData({ document_type: '', expiry_date: '', file: null });
      loadDocuments();
    } catch (err) {
      toast.error('Failed to upload document');
    }
  };
  
  // ==========================================================================
  // RENDER
  // ==========================================================================
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
      </div>
    );
  }
  
  if (error || !asset) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">{error || 'Asset Not Found'}</h2>
        <Button onClick={() => navigate('/assets')} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Assets
        </Button>
      </div>
    );
  }
  
  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/assets')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{asset.park_name}</h1>
            <p className="text-sm text-slate-500">{asset.pitch_or_unit || asset.address}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={asset.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
            {asset.status || 'Active'}
          </Badge>
          <Badge variant="outline">{asset.asset_type || 'Unit'}</Badge>
          {asset.heating_type && (
            <Badge className="bg-orange-100 text-orange-700">
              <Flame className="h-3 w-3 mr-1" />
              {asset.heating_type}
            </Badge>
          )}
        </div>
      </div>
      
      {/* Compliance Summary Strip */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-[#1F7A78]/5 to-transparent">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 text-sm">
            {/* EICR Due */}
            <div className={`p-3 rounded-lg ${eicrDue.isOverdue ? 'bg-red-50' : eicrDue.daysUntil !== null && eicrDue.daysUntil < 30 ? 'bg-amber-50' : 'bg-white'}`}>
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                <Zap className="h-3 w-3" />
                Next EICR Due
              </div>
              <p className={`font-semibold ${eicrDue.isOverdue ? 'text-red-600' : ''}`}>
                {eicrDue.date ? format(parseISO(eicrDue.date), 'dd MMM yyyy') : 'No Record'}
              </p>
            </div>
            
            {/* Gas Due */}
            <div className={`p-3 rounded-lg ${gasDue.isOverdue ? 'bg-red-50' : gasDue.daysUntil !== null && gasDue.daysUntil < 30 ? 'bg-amber-50' : 'bg-white'}`}>
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                <Flame className="h-3 w-3" />
                Next Gas Due
              </div>
              <p className={`font-semibold ${gasDue.isOverdue ? 'text-red-600' : ''}`}>
                {gasDue.date ? format(parseISO(gasDue.date), 'dd MMM yyyy') : 'No Record'}
              </p>
            </div>
            
            {/* Boiler Due */}
            <div className={`p-3 rounded-lg ${boilerDue.isOverdue ? 'bg-red-50' : boilerDue.daysUntil !== null && boilerDue.daysUntil < 30 ? 'bg-amber-50' : 'bg-white'}`}>
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                <Flame className="h-3 w-3" />
                Next Boiler Service
              </div>
              <p className={`font-semibold ${boilerDue.isOverdue ? 'text-red-600' : ''}`}>
                {boilerDue.date ? format(parseISO(boilerDue.date), 'dd MMM yyyy') : 'No Record'}
              </p>
            </div>
            
            {/* Open Jobs */}
            <div className={`p-3 rounded-lg ${openJobsCount > 0 ? 'bg-blue-50' : 'bg-white'}`}>
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                <Briefcase className="h-3 w-3" />
                Open Jobs
              </div>
              <p className="font-semibold text-blue-700">{openJobsCount}</p>
            </div>
            
            {/* Overdue PPM */}
            <div className={`p-3 rounded-lg ${overduePPMCount > 0 ? 'bg-red-50' : 'bg-white'}`}>
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                <AlertOctagon className="h-3 w-3" />
                Overdue PPM
              </div>
              <p className={`font-semibold ${overduePPMCount > 0 ? 'text-red-600' : ''}`}>{overduePPMCount}</p>
            </div>
            
            {/* Documents On File */}
            <div className="p-3 rounded-lg bg-white">
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                <FileText className="h-3 w-3" />
                Documents
              </div>
              <p className="font-semibold">{documents.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full flex">
          <TabsTrigger value="overview" className="flex-1">Overview</TabsTrigger>
          <TabsTrigger value="ppm" className="flex-1">PPM Schedule</TabsTrigger>
          <TabsTrigger value="jobs" className="flex-1">Jobs ({jobs.length})</TabsTrigger>
          <TabsTrigger value="documents" className="flex-1">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="activity" className="flex-1">Activity</TabsTrigger>
        </TabsList>
        
        {/* ================================================================
            OVERVIEW TAB
        ================================================================ */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Asset Details */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Asset Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-slate-500">Park / Site</Label>
                    <p className="font-medium">{asset.park_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Unit / Pitch</Label>
                    <p className="font-medium">{asset.pitch_or_unit || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Address</Label>
                    <p className="font-medium">{asset.address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Asset Type</Label>
                    <p className="font-medium">{asset.asset_type || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Heating Type</Label>
                    <p className="font-medium">{asset.heating_type || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Has Boiler</Label>
                    <p className="font-medium">{asset.has_boiler ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Occupancy</Label>
                    <p className="font-medium">{asset.occupancy_type || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Risk Level</Label>
                    <p className="font-medium">{asset.risk_level || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Quick Actions */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  className="w-full bg-[#1F7A78] hover:bg-[#16615f]"
                  onClick={() => navigate(`/fm?create=true&asset_id=${assetId}`)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Job for Asset
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setShowUploadDialog(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setActiveTab('ppm')}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  View PPM Schedule
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* ================================================================
            PPM SCHEDULE TAB
        ================================================================ */}
        <TabsContent value="ppm" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">PPM Schedule</CardTitle>
                <Button size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-1" />
                  Add PPM Item
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {ppmItems.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No PPM items scheduled for this asset</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ppmItems.map(ppm => {
                    const isOverdue = ppm.next_due_date && differenceInDays(new Date(), parseISO(ppm.next_due_date)) > 0;
                    return (
                      <div 
                        key={ppm.id} 
                        className={`p-4 rounded-lg border ${isOverdue ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{ppm.task_name_snapshot || ppm.ppm_type}</p>
                            <p className="text-sm text-slate-500">
                              Every {ppm.frequency_months} months
                              {ppm.mandatory_certificate && (
                                <Badge className="ml-2 bg-amber-100 text-amber-700">Cert Required</Badge>
                              )}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-medium ${isOverdue ? 'text-red-600' : ''}`}>
                              {ppm.next_due_date ? format(parseISO(ppm.next_due_date), 'dd MMM yyyy') : 'Not Set'}
                            </p>
                            {isOverdue && <Badge className="bg-red-100 text-red-700">Overdue</Badge>}
                          </div>
                        </div>
                        {ppm.linked_job_id && (
                          <Link 
                            to={`/fm/jobs/${ppm.linked_job_id}`}
                            className="text-xs text-[#1F7A78] flex items-center gap-1 mt-2"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View Linked Job
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ================================================================
            JOBS TAB
        ================================================================ */}
        <TabsContent value="jobs" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Jobs</CardTitle>
                <Button 
                  size="sm" 
                  className="bg-[#1F7A78] hover:bg-[#16615f]"
                  onClick={() => navigate(`/fm?create=true&asset_id=${assetId}`)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Job
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {jobs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No jobs for this asset</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {jobs.map(job => (
                    <Link 
                      key={job.id} 
                      to={`/fm/jobs/${job.id}`}
                      className="block p-4 rounded-lg border hover:border-[#1F7A78] transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{job.job_reference}</p>
                            <Badge className={STATUS_COLORS[job.status] || 'bg-gray-100'}>
                              {job.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">{job.job_type_label || job.job_type}</p>
                        </div>
                        <div className="text-right text-sm">
                          {job.scheduled_date && (
                            <p>{format(parseISO(job.scheduled_date), 'dd MMM yyyy')}</p>
                          )}
                          {job.assigned_contractor_name && (
                            <p className="text-slate-500">{job.assigned_contractor_name}</p>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ================================================================
            DOCUMENTS TAB
        ================================================================ */}
        <TabsContent value="documents" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Documents & Certificates</CardTitle>
                <Button 
                  size="sm" 
                  className="bg-[#1F7A78] hover:bg-[#16615f]"
                  onClick={() => setShowUploadDialog(true)}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No documents uploaded for this asset</p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowUploadDialog(true)}
                  >
                    Upload First Document
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Group by document type */}
                  {DOCUMENT_TYPES.map(type => {
                    const typeDocs = documents.filter(d => d.document_type === type);
                    if (typeDocs.length === 0) return null;
                    
                    const Icon = DOC_TYPE_ICONS[type] || FileText;
                    
                    return (
                      <div key={type} className="border rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-4 w-4 text-[#1F7A78]" />
                          <span className="font-medium">{type}</span>
                          <Badge variant="outline">{typeDocs.length}</Badge>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {typeDocs.map(doc => (
                            <div 
                              key={doc.id}
                              className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm"
                            >
                              <div>
                                <p className="font-medium truncate max-w-[200px]">{doc.file_name}</p>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  {doc.upload_date && (
                                    <span>Uploaded: {format(parseISO(doc.upload_date), 'dd MMM yyyy')}</span>
                                  )}
                                  {doc.expiry_date && (
                                    <span className={differenceInDays(parseISO(doc.expiry_date), new Date()) < 0 ? 'text-red-600' : ''}>
                                      Expires: {format(parseISO(doc.expiry_date), 'dd MMM yyyy')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm">
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ================================================================
            ACTIVITY TAB
        ================================================================ */}
        <TabsContent value="activity" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Activity Log</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {activity.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No activity recorded</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activity.map((entry, idx) => (
                      <div key={entry.id || idx} className="flex gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#1F7A78] mt-2 shrink-0"></div>
                        <div className="flex-1 pb-4 border-b border-dashed">
                          <p className="font-medium text-sm">{entry.action}</p>
                          <p className="text-sm text-slate-600">{entry.details}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {entry.timestamp && format(parseISO(entry.timestamp), 'dd MMM yyyy HH:mm')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Document Type *</Label>
              <Select 
                value={uploadData.document_type} 
                onValueChange={v => setUploadData({...uploadData, document_type: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expiry Date (if applicable)</Label>
              <Input 
                type="date"
                value={uploadData.expiry_date}
                onChange={e => setUploadData({...uploadData, expiry_date: e.target.value})}
              />
            </div>
            <div>
              <Label>File</Label>
              <Input 
                type="file"
                onChange={e => setUploadData({...uploadData, file: e.target.files[0]})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button className="bg-[#1F7A78] hover:bg-[#16615f]" onClick={handleUploadDocument}>
              <Upload className="h-4 w-4 mr-1" />
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
